import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from './auth.service';
import { IdleTimeoutService, IdleTimeoutReason } from './idle-timeout.service';
import { TokenValidationService } from './token-validation.service';
import { DialogService, eDialogType } from '@ds/ng-wt-dialog';
import { IdleTimeoutWarningDialogComponent, IdleWarningDialogData } from './idle-timeout-warning-dialog.component';
import { NgToastTranslocoService } from 'src/app/common/services/ng-toast-transloco/ng-toast-transloco.service';
import { idleTimeoutConfig } from 'src/app/const/constant';

/**
 * IdleTimeoutInitializer - Orchestrates idle timeout system initialization
 * 
 * Responsibilities:
 * 1. Initialize idle timeout monitoring when user is authenticated
 * 2. Listen for idle state changes
 * 3. Show warning dialog when user becomes idle
 * 4. Handle user actions (extend session, logout)
 * 5. Auto-logout on warning countdown expiration
 * 6. Start token validation checks
 * 7. Clean up on logout
 */
@Injectable({
  providedIn: 'root',
})
export class IdleTimeoutInitializer {
  private idleSubscription: Subscription | null = null;
  private warningDialogActive = false;
  private warningDialogRef: any = null;
  private isInitialized = false; // Track initialization state

  constructor(
    private authService: AuthService,
    private idleTimeoutService: IdleTimeoutService,
    private tokenValidationService: TokenValidationService,
    private dialogService: DialogService,
    private router: Router,
    private toast: NgToastTranslocoService
  ) {}

  /**
   * Initialize the idle timeout system
   * Call this during app initialization after user is authenticated
   * Safe to call multiple times - will skip if already initialized
   */
  public initialize(): void {
    console.log('[IdleTimeoutInitializer] Initializing idle timeout system');
    
    // Skip if already initialized (guard against multiple calls)
    if (this.isInitialized) {
      console.log('[IdleTimeoutInitializer] Already initialized - skipping re-initialization');
      return;
    }

    console.log('[IdleTimeoutInitializer] Configuration:', {
      idleTime: idleTimeoutConfig.idleTime + ' minutes',
      warningTime: idleTimeoutConfig.warningTime + ' minutes',
      checkInterval: idleTimeoutConfig.checkInterval + ' minutes',
    });

    // Verify user is authenticated
    if (!this.authService.canNavigateToDashboard()) {
      console.warn('[IdleTimeoutInitializer] User not authenticated - skipping initialization');
      return;
    }

    console.log('[IdleTimeoutInitializer] User authenticated - starting monitoring');

    // Mark as initialized
    this.isInitialized = true;

    // Start monitoring user activity
    this.idleTimeoutService.startMonitoring();

    // Start periodic token validation
    this.tokenValidationService.startValidation();

    // Listen for idle state changes
    this.setupIdleStateListener();
  }

  /**
   * Setup listener for idle state changes
   */
  private setupIdleStateListener(): void {
    // Unsubscribe from previous subscription if exists
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }

    this.idleSubscription = this.idleTimeoutService.getIdleState$().subscribe((isIdle) => {
      if (isIdle && !this.warningDialogActive) {
        console.log('[IdleTimeoutInitializer] Idle state detected - showing warning dialog');
        this.showIdleWarningDialog();
      }
    });
  }

  /**
   * Show the idle warning dialog
   * This dialog is non-dismissible and shows a countdown timer
   */
  private showIdleWarningDialog(): void {
    this.warningDialogActive = true;
    console.log('[IdleTimeoutInitializer] showIdleWarningDialog: Creating dialog data');

    const dialogData: IdleWarningDialogData = {
      onExtend: () => this.handleExtendSession(),
      onLogout: () => this.handleLogout('user_logout'),
      onCountdownEnd: () => this.handleLogout('idle_expired'),
    };

    console.log('[IdleTimeoutInitializer] showIdleWarningDialog: Dialog data created', dialogData);

    // Open non-dismissible, non-closeable dialog with backdrop
    // CRITICAL: enableClose=false prevents close button
    // Event blocking is handled in component via global listeners
    this.warningDialogRef = this.dialogService.open(
      IdleTimeoutWarningDialogComponent,
      {
        data: dialogData,
        width: '420px',
        enableClose: false,            // No close button (X)
        type: eDialogType.Flyout,
      }
    );

    console.log('[IdleTimeoutInitializer] showIdleWarningDialog: Dialog opened', this.warningDialogRef);

    // Show toast notification
    // this.toast.toastWarning('TOASTS.SESSION_EXPIRING_WARNING');
  }

  /**
   * Handle extend session action
   * Validates token and resets idle timer
   */
  private handleExtendSession(): void {
    console.log('[IdleTimeoutInitializer] Extending session');

    // Validate token before extending
    if (!this.authService.isValidToken('id_token')) {
      console.warn('[IdleTimeoutInitializer] Token is invalid - cannot extend session');
      this.handleLogout('invalid_token');
      return;
    }

    // Reset idle timer
    this.idleTimeoutService.startMonitoring();
    this.warningDialogActive = false;

    // Show confirmation toast
    this.toast.toastSuccess('TOASTS.SESSION_EXTENDED');
    console.log('[IdleTimeoutInitializer] Session extended - idle timer reset');
  }

  /**
   * Handle logout action
   * Clean up and navigate to login
   */
  private handleLogout(reason: string): void {
    console.log('[IdleTimeoutInitializer] Logout triggered - reason:', reason);
    this.warningDialogActive = false;

    // Show appropriate toast message based on reason
    switch (reason) {
      case 'idle_expired':
        this.toast.toastWarning('TOASTS.SESSION_EXPIRED');
        break;
      case 'user_logout':
        this.toast.toastSuccess('TOASTS.USER_LOGGED_OUT');
        break;
      case 'invalid_token':
        this.toast.toastError('TOASTS.INVALID_TOKEN');
        break;
      default:
        this.toast.toastWarning('TOASTS.SESSION_ENDED');
    }

    // CRITICAL: Stop all monitoring immediately to prevent dialog from showing again
    this.cleanup();

    // Perform logout
    this.authService.forceLogout(reason);
  }

  /**
   * Cleanup on user logout
   * Call this when user explicitly logs out
   * This method is idempotent and safe to call multiple times
   * Resets initialization flag so initialize() can be called again on next login
   */
  public cleanup(): void {
    console.log('[IdleTimeoutInitializer] Cleaning up idle timeout system');

    // Stop monitoring and validation
    this.idleTimeoutService.stopMonitoring();
    this.tokenValidationService.stopValidation();

    // Unsubscribe from listeners
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
      this.idleSubscription = null;
    }

    // Close warning dialog if open
    if (this.warningDialogRef) {
      try {
        this.warningDialogRef.close();
      } catch (error) {
        console.warn('[IdleTimeoutInitializer] Error closing dialog:', error);
      }
      this.warningDialogRef = null;
    }

    // Reset dialog active flag
    this.warningDialogActive = false;

    // Reset initialization flag so initialize() can be called again on next login
    this.isInitialized = false;
    
    // Clear session activity timestamps
    sessionStorage.removeItem('lastActivityTime');
    sessionStorage.removeItem('idle_warning_shown');
    
    console.log('[IdleTimeoutInitializer] Cleanup completed');
  }

  /**
   * Check if warning dialog is currently active
   */
  public isWarningDialogActive(): boolean {
    return this.warningDialogActive;
  }
}
